import type { SingleValue } from "react-select";

import PeriodRangeFilter from "@/components/PeriodRangeFilter";
import { SelectInput } from "components/Select";
import { Input } from "components/ui/input";
import { NumericInput } from "components/ui/numeric-input";

import {
  AMOUNT_MAX,
  AMOUNT_MAX_LENGTH,
  DEPOSIT_MAINTENANCE_LABELS,
  EMPTY_AMOUNT,
  FilterOption,
  LINK_FILTER_OPTIONS,
  NSU_MAX_LENGTH,
} from "../types";

interface DepositFiltersProps {
  readonly storeOptions: readonly FilterOption[];
  readonly storeCode: number | undefined;
  readonly isStoreLocked: boolean;
  readonly onStoreChange: (storeCode: number | undefined) => void;
  readonly startDate: string | undefined;
  readonly endDate: string | undefined;
  readonly onPeriodChange: (
    startDate: string | undefined,
    endDate: string | undefined,
  ) => void;
  readonly minAmount: number | undefined;
  readonly maxAmount: number | undefined;
  readonly onMinAmountChange: (value: number | undefined) => void;
  readonly onMaxAmountChange: (value: number | undefined) => void;
  readonly nsu: string;
  readonly onNsuChange: (value: string) => void;
  readonly linkFilter: string | undefined;
  readonly onLinkFilterChange: (value: string | undefined) => void;
}

const findOption = (
  options: readonly FilterOption[],
  value: string | undefined,
): FilterOption | null =>
  options.find((option: FilterOption) => option.value === value) ?? null;

const readOptionValue = (newValue: unknown): string | undefined =>
  (newValue as SingleValue<FilterOption>)?.value ?? undefined;

const toOptionalAmount = (value: number): number | undefined =>
  Number.isNaN(value) || value === EMPTY_AMOUNT ? undefined : value;

const fieldLabel = "text-xs text-slate-500 mb-1 block";
const fieldWidth = "w-56";
const amountWidth = "w-32";

function RequiredMark() {
  return <span className="text-red-500"> *</span>;
}

export function DepositFilters(props: DepositFiltersProps) {
  const currentStore: FilterOption | null = findOption(
    props.storeOptions,
    props.storeCode === undefined ? undefined : String(props.storeCode),
  );

  const handleStoreChange = (newValue: unknown): void => {
    const value: string | undefined = readOptionValue(newValue);
    const parsed: number = Number(value);
    props.onStoreChange(
      value !== undefined && !Number.isNaN(parsed) ? parsed : undefined,
    );
  };

  return (
    <div className="flex flex-wrap items-end gap-4 mt-4">
      <div className={fieldWidth}>
        <label className={fieldLabel}>
          {DEPOSIT_MAINTENANCE_LABELS.storeLabel}
          <RequiredMark />
        </label>
        <SelectInput
          options={props.storeOptions}
          value={currentStore}
          onChange={handleStoreChange}
          isClearable={!props.isStoreLocked}
          isDisabled={props.isStoreLocked}
          placeholder={DEPOSIT_MAINTENANCE_LABELS.storePlaceholder}
        />
      </div>

      <div className="flex flex-col">
        <label className={fieldLabel}>
          {DEPOSIT_MAINTENANCE_LABELS.periodLabel}
          <RequiredMark />
        </label>
        <PeriodRangeFilter
          start={props.startDate}
          end={props.endDate}
          onChange={props.onPeriodChange}
          label={DEPOSIT_MAINTENANCE_LABELS.periodPlaceholder}
          inline
        />
      </div>

      <div className={amountWidth}>
        <label className={fieldLabel} htmlFor="deposit-min-amount">
          {DEPOSIT_MAINTENANCE_LABELS.minAmountLabel}
        </label>
        <NumericInput
          id="deposit-min-amount"
          numberType="decimal"
          value={props.minAmount ?? EMPTY_AMOUNT}
          onChange={(value: number) =>
            props.onMinAmountChange(toOptionalAmount(value))
          }
          max={AMOUNT_MAX}
          maxLength={AMOUNT_MAX_LENGTH}
          placeholder={DEPOSIT_MAINTENANCE_LABELS.amountPlaceholder}
          className="h-8"
        />
      </div>

      <div className={amountWidth}>
        <label className={fieldLabel} htmlFor="deposit-max-amount">
          {DEPOSIT_MAINTENANCE_LABELS.maxAmountLabel}
        </label>
        <NumericInput
          id="deposit-max-amount"
          numberType="decimal"
          value={props.maxAmount ?? EMPTY_AMOUNT}
          onChange={(value: number) =>
            props.onMaxAmountChange(toOptionalAmount(value))
          }
          max={AMOUNT_MAX}
          maxLength={AMOUNT_MAX_LENGTH}
          placeholder={DEPOSIT_MAINTENANCE_LABELS.amountPlaceholder}
          className="h-8"
        />
      </div>

      <div className="w-40">
        <label className={fieldLabel} htmlFor="deposit-nsu">
          {DEPOSIT_MAINTENANCE_LABELS.nsuLabel}
        </label>
        <Input
          id="deposit-nsu"
          value={props.nsu}
          onChange={(event) => props.onNsuChange(event.target.value)}
          placeholder={DEPOSIT_MAINTENANCE_LABELS.nsuPlaceholder}
          maxLength={NSU_MAX_LENGTH}
          className="h-8 font-mono"
        />
      </div>

      <div className={fieldWidth}>
        <label className={fieldLabel}>
          {DEPOSIT_MAINTENANCE_LABELS.linkFilterLabel}
        </label>
        <SelectInput
          options={LINK_FILTER_OPTIONS}
          value={findOption(LINK_FILTER_OPTIONS, props.linkFilter)}
          onChange={(newValue: unknown) =>
            props.onLinkFilterChange(readOptionValue(newValue))
          }
          isClearable
          placeholder={DEPOSIT_MAINTENANCE_LABELS.allOption}
        />
      </div>
    </div>
  );
}
